<script setup lang="ts">
import { StructuredText, renderNodeRule } from 'vue-datocms'
import { isLink, isThematicBreak } from 'datocms-structured-text-utils'
import type {
  ArticleModelBodyField,
  SectionStructuredTextModelStructuredTextField
} from '~/types/datocms'

import {
  Button,
  DatoLink,
  HorizontalRule,
  Image,
  ImageGallery,
  NuxtLink,
  LazyMediaBlock,
  LazyIframeBlock,
  LazyTableBlock,
  LazyVideoExternalBlock
} from '#components'

defineProps<{
  data: ArticleModelBodyField | SectionStructuredTextModelStructuredTextField
}>()

const customNodeRules = [
  renderNodeRule(isLink, ({ node, key }) => h(DatoLink, { key, node })),
  renderNodeRule(isThematicBreak, ({ node, key }) => h(HorizontalRule, { key, node }))
]

const renderInlineRecord = ({ record }: unknown) => {
  return h(
    Button,
    { to: useLocalePathByType(record._modelApiKey, record.slug) },
    () => record.title
  )
}

const renderLinkToRecord = ({ record, children }: unknown) => {
  return h(NuxtLink, { to: useLocalePathByType(record._modelApiKey, record.slug) }, () => children)
}

function renderBlock({ record }: { record: unknown }) {
  switch (record.__typename) {
    case 'ButtonRecord':
      return h(Button, {
        label: record.label,
        variant: record.variant,
        to:
          record.reference !== null
            ? useLocalePathByType(record.reference._modelApiKey, record.reference.slug)
            : record.externalLink,
        target: record.externalLink && '_blank'
      })
    case 'ImageRecord':
      return h(Image, {
        src: record.image.responsiveImage ? record.image.responsiveImage?.src : record.image.url,
        srcSet: record.image.responsiveImage ? record.image.responsiveImage?.webpSrcSet : undefined,
        width: record.image.responsiveImage
          ? record.image.responsiveImage?.width
          : record.image.width,
        height: record.image.responsiveImage
          ? record.image.responsiveImage?.height
          : record.image.height,
        alt: record.image.alt
      })
    case 'MediaBlockRecord':
      return h(LazyMediaBlock, {
        data: record
      })
    case 'IframeRecord':
      return h(LazyIframeBlock, {
        data: record
      })
    case 'ImageGalleryRecord':
      return h(ImageGallery, {
        data: record.images
      })
    case 'TableRecord':
      return h(LazyTableBlock, {
        data: record
      })
    case 'VideoExternalRecord':
      return h(LazyVideoExternalBlock, {
        data: record
      })
  }
}
</script>

<template>
  <StructuredText
    :data="data"
    :custom-node-rules="customNodeRules"
    :render-block="renderBlock"
    :render-inline-record="renderInlineRecord"
    :render-link-to-record="renderLinkToRecord"
  />
</template>
